export default function filtersComponent() {
    return {
        activeFilters: [],
        isCumulative: false,
        animating: false,
        expandedParents: new Set(),
        filterHierarchy: {},
        hasVisibleItems: true,
        cachedElements: new Map(),
        
        init() {
            const { dataset } = this.$el;
            
            // Initialize from URL parameters or dataset
            this.initializeFromURL(dataset);
            this.buildFilterHierarchy();
            this.cacheElements();
            
            // Batch initial updates
            requestAnimationFrame(() => {
                this.updateActiveFilterClasses();
                this.updateExpandedStates();
                this.updateURLState();
                
                // Defer non-critical operations
                setTimeout(() => {
                    this.checkVisibleItems();
                    this.updateProjectLinks();
                }, 50);
            });
            
            this.setupListeners();
        },
        
        initializeFromURL(dataset) {
            const urlParams = new URLSearchParams(window.location.search);
            const urlFilters = urlParams.get('filters');
            const urlExpanded = urlParams.get('expanded');
            const urlCumulative = urlParams.get('cumulative');

            // Decode %2C to comma for correct splitting
            const decodedFilters = urlFilters ? urlFilters.replace(/%2C/gi, ',') : null;
            const decodedExpanded = urlExpanded ? urlExpanded.replace(/%2C/gi, ',') : null;

            // Use URL parameters if available, otherwise fall back to dataset
            if (decodedFilters) {
                this.activeFilters = decodedFilters.split(',');
            } else {
                this.activeFilters = dataset.initialFilter?.trim()?.split(/\s+/) || [];
            }

            // Restore expanded state from URL
            if (decodedExpanded) {
                this.expandedParents = new Set(decodedExpanded.split(','));
            }

            // Restore cumulative mode from URL
            this.isCumulative = urlCumulative === '1' || dataset.cumulative === "true";
        },
        
        cacheElements() {
            // Cache frequently accessed elements for better performance
            this.cachedElements.set('wrapper', this.$refs.wrapper);
            this.cachedElements.set('filters', this.$el.querySelectorAll('.filter'));
            this.cachedElements.set('filterParents', this.$el.querySelectorAll('.filter-parent'));
            this.cachedElements.set('filterChildren', this.$el.querySelectorAll('.filter-children'));
        },
        
        setupListeners() {
            // Use passive listeners for better performance
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.expandedParents.size > 0) {
                    this.expandedParents.clear();
                    this.updateExpandedStates();
                }
            }, { passive: true });
        },
        
        buildFilterHierarchy() {
            const hierarchyData = this.$el.dataset.filterHierarchy;

            if (hierarchyData) {
                try {
                    const cleanedData = hierarchyData
                        .trim()
                        .replace(/,\s*]/g, ']')
                        .replace(/,\s*}/g, '}');

                    this.filterHierarchy = JSON.parse(cleanedData);
                } catch (e) {
                    this.filterHierarchy = {};
                }
            } else {
                this.filterHierarchy = {};
            }
            // Build reverse map child -> parent for quick lookup
            this.parentMap = {};
            Object.keys(this.filterHierarchy).forEach(parent => {
                (this.filterHierarchy[parent] || []).forEach(child => {
                    this.parentMap[child] = parent;
                });
            });
        },
        
        updateURLState() {
            const url = new URL(window.location);
            
            // Clear existing filter parameters
            url.searchParams.delete('filters');
            url.searchParams.delete('expanded');
            url.searchParams.delete('cumulative');
            
            // Add current filter state
            if (this.activeFilters.length > 0 && !this.activeFilters.includes('all')) {
                url.searchParams.set('filters', this.activeFilters.join(','));
            }
            
            if (this.expandedParents.size > 0) {
                url.searchParams.set('expanded', Array.from(this.expandedParents).join(','));
            }
            
            if (this.isCumulative) {
                url.searchParams.set('cumulative', '1');
            }
            
            // Update URL without triggering page reload
            const newUrl = url.searchParams.toString() ? 
                `${url.pathname}?${url.searchParams.toString()}` : 
                url.pathname;
                
            window.history.replaceState({}, '', newUrl);
        },
        
        async setFilter(filter, isChild = false, parentFilter = null) {
            if (this.animating) return;
            
            const hasChildren = this.filterHierarchy[filter]?.length > 0;
            this.animating = true;
            
            const wrapper = this.cachedElements.get('wrapper');
            if (!wrapper) {
                this.animating = false;
                return;
            }
            
            // Optimized animation with CSS classes
            wrapper.classList.add('fade-slide-out');
            
            await this.delay(200);
            
            // Update filters while hidden
            if (filter === "all") {
                this.activeFilters = ["all"];
                this.expandedParents.clear();
            } else if (isChild) {
                this.handleChildFilter(filter, parentFilter);
            } else {
                this.handleParentFilter(filter);
            }
            
            // Batch DOM updates
            requestAnimationFrame(() => {
                this.updateActiveFilterClasses();
                this.updateExpandedStates();
                this.updateURLState();
                
                // Animate back in
                wrapper.classList.remove('fade-slide-out');
                wrapper.classList.add('fade-slide-in');
                
                setTimeout(() => {
                    wrapper.classList.remove('fade-slide-in');
                    this.animating = false;
                    
                    // Post-animation updates
                    this.checkVisibleItems();
                    this.updateProjectLinks();
                }, 250);
            });
        },
        
        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        
        handleParentFilter(filter) {
            const hasChildren = this.filterHierarchy[filter]?.length > 0;
            
            // Remove "all" from activeFilters
            this.activeFilters = this.activeFilters.filter(f => f !== "all");
            
            if (hasChildren) {
                if (this.expandedParents.has(filter)) {
                    // Collapse
                    this.expandedParents.delete(filter);
                    this.activeFilters = this.activeFilters.filter(f => {
                        return f !== filter && !this.filterHierarchy[filter]?.includes(f);
                    });
                } else {
                    // Expand
                    this.clearOtherParentFilters(filter);
                    this.expandedParents.clear();
                    this.expandedParents.add(filter);
                    this.activeFilters.push(filter);
                }
            } else {
                // Regular filter without children
                this.expandedParents.clear();
                
                if (this.isCumulative) {
                    const index = this.activeFilters.indexOf(filter);
                    if (index > -1) {
                        this.activeFilters.splice(index, 1);
                    } else {
                        this.activeFilters.push(filter);
                    }
                } else {
                    this.activeFilters = [filter];
                }
            }
            
            // Fallback to "all" if no filters active
            if (this.activeFilters.length === 0 && this.expandedParents.size === 0) {
                this.activeFilters = ["all"];
            }
        },
        
        clearOtherParentFilters(currentFilter) {
            const allOtherParentFilters = [];
            Object.entries(this.filterHierarchy).forEach(([parentFilter, children]) => {
                if (parentFilter !== currentFilter) {
                    allOtherParentFilters.push(parentFilter, ...children);
                }
            });
            this.activeFilters = this.activeFilters.filter(f => !allOtherParentFilters.includes(f));
        },
        
        handleChildFilter(filter, parentFilter) {
            if (parentFilter) {
                this.expandedParents.add(parentFilter);
            }
            
            if (this.isCumulative) {
                const index = this.activeFilters.indexOf(filter);
                if (index > -1) {
                    this.activeFilters.splice(index, 1);
                } else {
                    this.activeFilters = [...this.activeFilters.filter(f => f !== "all"), filter];
                }
            } else {
                this.activeFilters = [filter];
            }
            
            if (this.activeFilters.length === 0) {
                this.activeFilters = ["all"];
                this.expandedParents.clear();
            }
        },
        
        isParentExpanded(filter) {
            return this.expandedParents.has(filter)
                || this.activeFilters.includes(filter)
                || this.hasActiveChildren(filter);
        },
        
        isVisible(filterClasses) {
            if (this.activeFilters.includes("all")) {
                return true;
            }
            
            if (!filterClasses || filterClasses.length === 0) {
                return false;
            }
            
            // Optimized visibility check
            for (const activeFilter of this.activeFilters) {
                if (filterClasses.includes(activeFilter)) {
                    return true;
                }
                
                const childFilters = this.filterHierarchy[activeFilter] || [];
                if (childFilters.some(child => filterClasses.includes(child))) {
                    return true;
                }
            }
            
            return false;
        },
        
        checkVisibleItems() {
            const wrapper = this.cachedElements.get('wrapper');
            if (!wrapper) {
                this.hasVisibleItems = true;
                return;
            }
            
            // Optimized element selection
            const selectors = [
                '[class*="filter-"]',
                '.filter-item',
                '[data-filter]',
                '.page-link',
                'article',
                '.card'
            ];
            
            let allItems = [];
            
            for (const selector of selectors) {
                const found = wrapper.querySelectorAll(selector);
                if (found.length > 0) {
                    allItems = Array.from(found);
                    break;
                }
            }
            
            if (allItems.length === 0) {
                this.hasVisibleItems = true;
                return;
            }
            
            let visibleCount = 0;
            
            // Optimized visibility checking
            allItems.forEach(item => {
                let filterClasses = Array.from(item.classList)
                    .filter(cls => cls.startsWith('filter-'));
                
                if (item.dataset.filter) {
                    filterClasses = filterClasses.concat(item.dataset.filter.split(' '));
                }
                
                filterClasses = [...new Set(filterClasses)];
                
                const itemVisible = filterClasses.length > 0 ? this.isVisible(filterClasses) : true;
                
                if (itemVisible) {
                    visibleCount++;
                }
            });
            
            this.hasVisibleItems = visibleCount > 0;
        },
        
        updateProjectLinks() {
            const wrapper = this.cachedElements.get('wrapper');
            if (!wrapper) return;
            
            const selectors = [
                'a[href*="/work/"]',
                'a[href*="/projects/"]',
                '.project-link',
                '.card a',
                'article a',
                '[class*="filter-"] a'
            ];
            
            let projectLinks = [];
            
            for (const selector of selectors) {
                const found = wrapper.querySelectorAll(selector);
                if (found.length > 0) {
                    projectLinks = Array.from(found);
                    break;
                }
            }
            
            if (projectLinks.length === 0) return;
            
            // Batch link updates
            const filterParam = this.buildFilterParam();
            
            projectLinks.forEach(link => {
                const originalHref = link.getAttribute('data-original-href') || link.href;
                
                if (!link.getAttribute('data-original-href')) {
                    link.setAttribute('data-original-href', originalHref);
                }
                
                if (filterParam) {
                    const separator = originalHref.includes('?') ? '&' : '?';
                    link.href = `${originalHref}${separator}${filterParam}`;
                } else {
                    link.href = originalHref;
                }
            });
        },
        
        buildFilterParam() {
            if (this.activeFilters.length === 0 || this.activeFilters.includes('all')) {
                return '';
            }
            
            const params = new URLSearchParams();
            params.set('filters', this.activeFilters.join(','));
            
            if (this.expandedParents.size > 0) {
                params.set('expanded', Array.from(this.expandedParents).join(','));
            }
            
            if (this.isCumulative) {
                params.set('cumulative', '1');
            }
            
            return params.toString();
        },
        
        clearAllFilters() {
            this.setFilter('all');
        },
        
        hasActiveChildren(parentFilter) {
            const children = this.filterHierarchy[parentFilter];
            return children?.some(childFilter => this.activeFilters.includes(childFilter)) || false;
        },
        
        getActiveChildCount(parentFilter) {
            const children = this.filterHierarchy[parentFilter];
            return children?.filter(childFilter => this.activeFilters.includes(childFilter)).length || 0;
        },
        
        updateActiveFilterClasses() {
            const filters = this.cachedElements.get('filters');
            const filterParents = this.cachedElements.get('filterParents');

            // Child / standalone filters (elements with `.filter`)
            filters.forEach(el => {
                const token = el.dataset.filter;
                const parent = this.parentMap ? this.parentMap[token] : null;
                const isSelected = this.activeFilters.includes(token) || (parent && this.activeFilters.includes(parent));
                el.classList.toggle("filter-active", isSelected);
                el.setAttribute("aria-pressed", isSelected);
            });

            // Parent pills (elements with `.filter-parent`)
            filterParents.forEach(el => {
                const token = el.dataset.filter;
                const children = this.filterHierarchy[token] || [];
                const isSelected = this.activeFilters.includes(token);
                const hasActiveChildren = children.some(child => this.activeFilters.includes(child));
                const isActive = isSelected || hasActiveChildren;

                el.classList.toggle("filter-active", isActive);
                el.classList.toggle("has-active-children", hasActiveChildren);
                el.setAttribute("aria-pressed", isActive);
            });
        },
        
        updateExpandedStates() {
            const filterParents = this.cachedElements.get('filterParents');
            const filterChildren = this.cachedElements.get('filterChildren');

            // Batch parent updates
            filterParents.forEach(el => {
                const filter = el.dataset.filter;
                const isExpanded = this.isParentExpanded(filter);
                el.classList.toggle("expanded", isExpanded);
                el.setAttribute("aria-expanded", isExpanded);
            });

            // Batch children updates
            filterChildren.forEach(el => {
                const parentFilter = el.dataset.parent;
                const isExpanded = this.isParentExpanded(parentFilter);

                el.classList.toggle('expanded', isExpanded);
                el.setAttribute("aria-hidden", !isExpanded);

                if (isExpanded) {
                    el.style.display = "block";
                    el.classList.remove('hide');
                    el.offsetHeight; // Trigger reflow
                    el.classList.add('show');
                } else {
                    el.classList.remove('show');
                    el.classList.add('hide');
                    setTimeout(() => {
                        if (!this.expandedParents.has(parentFilter)) {
                            el.style.display = "none";
                        }
                    }, 300);
                }
            });
        }
    };
}